import { useQuery } from "@tanstack/react-query";
import { useMemo, type ReactNode } from "react";
import { portalFetch } from "../api/http";
import { teamSharedKeys, text, type TeamUi } from "../team/teamText";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { ChatIcon, ShellIcon } from "../ui/icons/Icon";

// Piezas de la cuenta propia (Mi perfil, Preferencias, Seguridad) y de Ajustes de la empresa, con el sistema de
// Configuración (team.css): marco y toast de usersUi, encabezado C03, secciones con título, selector de zona con búsqueda
// (C08 > 6 opciones) y la fila que lleva a otra pantalla (C17). Lo que cada persona puede hacer lo decide el servidor.

/** Textos de estas pantallas: los del portal actual que se reutilizan más los nuevos ACC_. */
export const accountPrefixes = [
  ...teamSharedKeys,
  "ACC_",
  "CFG1_",
  "NAV_PROFILE",
  "NAV_PREFS",
  "SETTINGS_TITLE",
  "REG_FIRST",
  "REG_LAST",
  "FEEDBACK_FIRST_REQUIRED",
  "FEEDBACK_LAST_REQUIRED",
  "CHAT_R3_TIMEZONE_INVALID",
  "PROFILE_",
  "PREFS_",
  "THEME_",
  "LANG_",
  "SEC_",
  "PWD_",
  "CP_PASSWORD_RULE",
  "MFA_",
  "DEV_ONLY",
  "EVT_",
  "CP_EVT_",
  "USAGE_UNKNOWN",
  "CP_SESSION_CURRENT",
  "CP_SECURITY_ROW_GONE",
  "SUPPORT_SECURITY_BLOCKED",
  "TEAM_ERR_",
  "CP_ACTION_UNAVAILABLE",
  "CP_AUTH_DATA_REJECTED",
  "REG_ERROR_FIELDS",
  "COMPANY_",
  "INBOX_STATUS_",
  "CUR_",
  "ACTION_SAVE",
  "ACTION_CANCEL",
  "LIST_SHOW",
];

const theme = (): "light" | "dark" =>
  document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";

/** El contexto de las piezas del kit con los textos de la cuenta (una sola lectura por idioma). */
export function useAccountUi(locale: string): TeamUi | null {
  const texts = useQuery({
    queryKey: ["texts", locale, "account"],
    queryFn: () =>
      portalFetch<Record<string, string>>(
        `/api/portal/texts/${locale}?prefixes=${encodeURIComponent(accountPrefixes.join(","))}`,
      ),
  });
  return useMemo<TeamUi | null>(
    () =>
      texts.data ? { labels: texts.data, locale, timeZone: "UTC", userId: 0, organizationId: 0, theme: theme() } : null,
    [texts.data, locale],
  );
}

/** Encabezado C03 de una pantalla de la cuenta: ← solo si reemplaza a otra (Seguridad vuelve a Mi perfil). */
export function AccountHead({
  ui,
  title,
  sub,
  back,
}: {
  ui: TeamUi;
  title: string;
  sub?: string;
  back?: { href: string; labelKey: string };
}) {
  return (
    <header className="tm-head" data-part="encabezado">
      <div className="tm-head-title">
        {back ? (
          <a
            className="tm-back"
            href={back.href}
            aria-label={text(ui, back.labelKey)}
            title={text(ui, back.labelKey)}
            data-part="atras"
          >
            <ChatIcon name="arrow-left" />
          </a>
        ) : null}
        <div>
          <h1>{title}</h1>
          {sub ? <p className="tm-head-sub">{sub}</p> : null}
        </div>
      </div>
    </header>
  );
}

/** Sección con título h2 y, si hace falta, su acción a la derecha y una línea que explica para qué sirve. */
export function AccountSection({
  id,
  title,
  line,
  action,
  part,
  children,
}: {
  id: string;
  title: string;
  line?: string;
  action?: ReactNode;
  part: string;
  children: ReactNode;
}) {
  return (
    <section className="tm-inbox-section acc-section" aria-labelledby={id} data-part={part}>
      <div className="tm-section-head">
        <h2 id={id} className="tm-subtitle">
          {title}
        </h2>
        {action}
      </div>
      {line ? <p className="tm-hint">{line}</p> : null}
      {children}
    </section>
  );
}

export type Zone = { id: string; label: string };

/** Zona horaria con búsqueda (más de 300 opciones): «Lima (GMT-5)», como el resto del portal. */
export function ZoneSelect({
  ui,
  id,
  label,
  value,
  zones,
  error,
  help,
  onChange,
}: {
  ui: TeamUi;
  id: string;
  label: string;
  value: string;
  zones: Zone[];
  error?: string;
  help?: string;
  onChange: (value: string) => void;
}) {
  const options = useMemo(() => zones.map((zone) => ({ value: zone.id, label: zone.label })), [zones]);
  const described = [error ? `${id}-error` : null, help ? `${id}-help` : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={`tm-field ${error ? "invalid" : ""}`} data-field="timezone" data-part="zona">
      <span id={`${id}-label`}>{label}</span>
      <ChoiceMenu
        id={id}
        label={label}
        describedBy={described}
        value={value}
        onChange={onChange}
        options={options}
        searchLabel={text(ui, "CFG1_SEARCH")}
        clearSearchLabel={text(ui, "CFG1_R1_CLEAR_SEARCH")}
        searchIcon={<ChatIcon name="search" />}
        clearIcon={<ChatIcon name="x" />}
        chevron={<ChatIcon name="chevron-down" />}
        check={<ChatIcon name="check" />}
      />
      {error ? (
        <span id={`${id}-error`} className="tm-field-error">
          {error}
        </span>
      ) : null}
      {help ? (
        <small id={`${id}-help`} className="tm-field-help">
          {help}
        </small>
      ) : null}
    </div>
  );
}

/** Fila C17 que lleva a otra pantalla de la cuenta: ícono, título, para qué sirve y la flecha. */
export function AccountLink({ href, icon, title, hint }: { href: string; icon: string; title: string; hint: string }) {
  return (
    <a className="rowline link" href={href}>
      <span className="cfg1-config-entry">
        <ShellIcon name={icon} />
        <span>
          <strong>{title}</strong>
          <small className="trace">{hint}</small>
        </span>
      </span>
      <ShellIcon name="chevron-right" />
    </a>
  );
}

/** Sin sesión: al login, volviendo a esta misma pantalla después. */
export function goLogin(): void {
  window.location.assign(`/login?ReturnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
}
